import { Check, Layers, Lock, Sparkles } from "lucide-react";
import useTranslation from "../../client/hooks/useTranslation";
import { Button } from "../../client/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../client/components/ui/card";
import { useUiMode } from "../mode/useUiMode";
import type { UiMode } from "../mode/uiMode";

/** Carte « Mode d'affichage » de la page Réglages (visible dans les deux modes). */
export function DisplayModeCard({ onUpgrade }: { onUpgrade: () => void }) {
  const t = useTranslation().lite.mode;
  const { enabled, mode, canUseAdvanced, setMode } = useUiMode();
  if (!enabled) return null;
  const modes: { id: UiMode; title: string; desc: string; points: string[]; icon: typeof Sparkles }[] = [
    { id: "lite", title: t.lite, desc: t.liteDesc, points: t.litePoints, icon: Sparkles },
    { id: "advanced", title: t.advanced, desc: t.advancedDesc, points: t.advancedPoints, icon: Layers },
  ];
  return (
    <Card className="mb-4 lg:m-8">
      <CardHeader>
        <CardTitle className="text-foreground text-base font-semibold leading-6">{t.title}</CardTitle>
        <p className="text-sm text-muted-foreground">{canUseAdvanced ? t.subtitlePaid : t.subtitleFree}</p>
      </CardHeader>
      <CardContent>
        <div className="grid sm:grid-cols-2 gap-3">
          {modes.map(m => {
            const active = mode === m.id;
            const locked = m.id === "advanced" && !canUseAdvanced;
            return (
              <button
                key={m.id}
                type="button"
                aria-pressed={active}
                onClick={() => (locked ? onUpgrade() : setMode(m.id))}
                className={`text-left rounded-xl border p-4 transition-colors ${active ? "border-primary bg-primary/5" : "border-border hover:bg-muted/60"}`}
              >
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-2 font-semibold"><m.icon size={16} className="text-primary" />{m.title}</span>
                  {active ? <Check size={16} className="text-primary" /> : locked ? <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground"><Lock size={12} /> {t.lockedBadge}</span> : null}
                </div>
                <p className="text-sm text-muted-foreground mt-1">{m.desc}</p>
                <ul className="mt-3 space-y-1">{m.points.map(p => <li key={p} className="text-xs text-muted-foreground/80">· {p}</li>)}</ul>
              </button>
            );
          })}
        </div>
        {!canUseAdvanced && <Button className="mt-4" onClick={onUpgrade}>{t.upgrade}</Button>}
      </CardContent>
    </Card>
  );
}
